import React, {FC, lazy, Suspense, useEffect} from 'react';
import {BrowserRouter as Router, Redirect, Route, Switch} from 'react-router-dom';

import {Layout, Loader, QueryPreservingRoute} from 'components';

import {ROUTES} from 'constants/routes';
import {languageFromPath} from 'i18n/detection';
import {faqFilters, FaqFilterType} from 'types/faq';

import Home from './Home';

/**
 * 路由级代码分割（2026-10）：除首页外全部页面 React.lazy 化，webpack 4 按 dynamic import 拆 chunk，
 * 首屏不再加载全部页面的代码（Faq 词典 / Guide 长文 / ApiDoc 等重内容页不再互相拖累，Core Web Vitals 收益）。
 * Home 保持同步 import：首页是流量入口，lazy 会让首拍多一次 chunk 请求，得不偿失。
 * faqFilters/FaqFilterType 改从 types/faq 导入而非 ./Faq 的 re-export——
 * 静态具名 import 会把整个 Faq 模块（组件+样式+词典依赖）保留在主包，让 lazy 化失效。
 */
const AboutUsEn = lazy(() => import('./AboutUsEn'));
const ApiDoc = lazy(() => import('./ApiDoc'));
const ContactUs = lazy(() => import('./ContactUs'));
const DeliveryPolicy = lazy(() => import('./DeliveryPolicy'));
const DigitalMarketing = lazy(() => import('./DigitalMarketing'));
const Extension = lazy(() => import('./Extension'));
const Faq = lazy(() => import('./Faq'));
const Guide = lazy(() => import('./Guide'));
const ImageFusion = lazy(() => import('./ImageFusion'));
const ImageTranslator = lazy(() => import('./ImageTranslator'));
const Languages = lazy(() => import('./Languages'));
const ListingImages = lazy(() => import('./ListingImages'));
const NotFound = lazy(() => import('./NotFound'));
const Pricing = lazy(() => import('./Pricing'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const ProxyMechanism = lazy(() => import('./ProxyMechanism'));
const ReturnPolicy = lazy(() => import('./ReturnPolicy'));
const SubscriptionAgreement = lazy(() => import('./SubscriptionAgreement'));
const TermsOfUse = lazy(() => import('./TermsOfUse'));
const VideoTranslator = lazy(() => import('./VideoTranslator'));

interface GoogleAnalyticsWindow extends Window {
  ga: any;
  gtag: any;
}

declare const window: GoogleAnalyticsWindow;

/**
 * 路由 basename:正式构建 /home,build:test 通过 REACT_APP_PUBLIC_PATH 注入 /home-test(见 config-overrides.js)。
 * 多语言独立 URL（2026-10）：非中文语言追加前缀段（/home/en、/home/ko、/home/ru），挂载时从
 * URL 解析一次——语言切换走整页跳转（LanguageSwitcher），不在此响应运行时变化。
 * basename 含语言后，站内所有相对路由链接自动携带前缀，无需逐个改造。
 */
const pathLanguage = languageFromPath(window.location.pathname);
const appBasename = `${process.env.REACT_APP_PUBLIC_PATH || '/home'}${pathLanguage ? `/${pathLanguage}` : ''}`;

const App: FC = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    if (utmSource) {
      sessionStorage.setItem('XJ_UTM_SOURCE', utmSource);
    }
  }, []);
  const renderGoogleAnalytics = () => {
    if (process.env.NODE_ENV === 'development') return null;
    return (
      <Route
        path="/"
        render={({location}) => {
          if (typeof window.ga === 'function') {
            window.gtag('config', 'UA-56989641-1', {
              page_location: window.location.href,
              page_path: location.pathname,
              page_title: document.title,
            });
          }
          return null;
        }}
      />
    );
  };

  return (
    <Router basename={appBasename}>
      {renderGoogleAnalytics()}
      <Layout>
        {/* Suspense 包住 Switch：lazy 页面 chunk 加载期间显示 Loader（页面切换短暂转圈） */}
        <Suspense fallback={<Loader />}>
          <Switch>
            <QueryPreservingRoute exact path="/" component={Home} />
            {/* i18n 前的中英分离旧路由（-cn 后缀）收敛到无后缀路径；SEO 权威 301 在 nginx（见 CLAUDE.md） */}
            <Redirect exact from="/about-us-cn" to={ROUTES.aboutUsEn} />
            <Redirect exact from={ROUTES.faq} to={`${ROUTES.faq}/${faqFilters[FaqFilterType.all]}`} />
            <QueryPreservingRoute exact path={`${ROUTES.faq}/:filter`} component={Faq} />
            <QueryPreservingRoute path={ROUTES.privacyPolicy} component={PrivacyPolicy} />
            <QueryPreservingRoute exact path={ROUTES.contact} component={ContactUs} />
            <QueryPreservingRoute exact path={ROUTES.termsOfUse} component={TermsOfUse} />
            <QueryPreservingRoute exact path={ROUTES.returnPolicy} component={ReturnPolicy} />
            <QueryPreservingRoute exact path={ROUTES.deliveryPolicy} component={DeliveryPolicy} />
            <QueryPreservingRoute exact path={ROUTES.subscriptionAgreement} component={SubscriptionAgreement} />
            {/* 文档页（迁移自老 /doc/ Docusaurus 站）：api-doc 为无导航入口备份页，导航 API 对接仍走 Apifox */}
            <QueryPreservingRoute exact path={ROUTES.apiDoc} component={ApiDoc} />
            <QueryPreservingRoute exact path={ROUTES.guide} component={Guide} />
            <QueryPreservingRoute exact path={ROUTES.proxyMechanism} component={ProxyMechanism} />
            <QueryPreservingRoute exact path={ROUTES.language} component={Languages} />
            <Redirect exact from="/contact-cn" to={ROUTES.contact} />
            <QueryPreservingRoute exact path={ROUTES.aboutUsEn} component={AboutUsEn} />
            <QueryPreservingRoute exact path={ROUTES.price} component={Pricing} />
            <Route exact path={ROUTES.imageTranslator} component={ImageTranslator} />
            <Route exact path={ROUTES.videoTranslator} component={VideoTranslator} />
            {/* 主图/详情图生成落地页（导航 detailPage 外链收编站内，2026-09） */}
            <Route exact path={ROUTES.listingImages} component={ListingImages} />
            {/* 商品图片生成 / 跨境数字化营销：导航原外链工作台 / 占位死链收编为站内落地页，2026-09 */}
            <Route exact path={ROUTES.fusion} component={ImageFusion} />
            <Route exact path={ROUTES.marketing} component={DigitalMarketing} />
            <QueryPreservingRoute exact path={ROUTES.extension} component={Extension} />
            {/* 未知路由渲染 404 页（带 noindex），不再静默跳首页造成 soft 404 */}
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </Layout>
    </Router>
  );
};

export default App;
